import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { BroadcastService } from "../broadcast.service";
import { FileSaverService } from "ngx-filesaver";
import { ApiService } from "src/app/api.service";
import moment from "moment";
import { UtilService } from "src/app/util/util.service";

@Component({
  selector: "app-broadcast-performance",
  templateUrl: "./broadcast-performance.component.html",
  styleUrls: ["./broadcast-performance.component.scss"],
})
export class BroadcastPerformanceComponent implements OnInit {
  searchValue = "";

  columns = [
    { code: "name", name: "Name", filter: true, selected: true },
    {
      code: "send_date_time",
      name: "Date & Time Sent",
      filter: true,
      selected: true,
    },
    { code: "sent", name: "Recipients", filter: true, selected: true },
    {
      code: "tracking_window",
      name: "Tracking window",
      filter: true,
      selected: true,
    },
    {
      code: "conversions_total",
      name: "Conversions",
      filter: true,
      selected: true,
    },
    {
      code: "conversion_pct",
      name: "Conversions Pct",
      filter: true,
      selected: true,
    },
    {
      code: "conversion_rank",
      name: "Conversions Rank",
      filter: true,
      selected: true,
    },
  ];
  displayedColumns = this.columns.map((column) => column.code);

  broadcastsDataSource!: MatTableDataSource<any>;
  @ViewChild("broadcastsTable") broadcastsTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  options!: any;
  broadcasts: any;
  selectedBroadcast: any;
  notFirst: any;
  filterForm!: FormGroup;
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private BroastcastService: BroadcastService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private Dialog: MatDialog,
    private uiService: UiService,
    private fileSaver: FileSaverService,
    private APIService: ApiService,
    private utilSvc: UtilService,
  ) {}

  ngOnInit(): void {
    const startDate = new Date();
    startDate.setMonth(new Date().getMonth() - 1);

    const endDate = new Date();

    this.filterForm = new FormGroup({
      startDate: new FormControl(startDate),
      endDate: new FormControl(endDate),
    });

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (this.filterForm.valid) {
          this.filterForm.controls.endDate.value.setHours(23, 59, 59);

          this.uiService
            .waitForEvent(
              this.BroastcastService.getBroadcasts(
                this.SessionService.currentAccountValue!.accountID,
                this.SessionService.currentAppValue!.id,
                {
                  startDate: this.filterForm.controls.startDate.value,
                  endDate: this.filterForm.controls.endDate.value,
                  facility_id: this.facilityIDs,
                  page: 0,
                  perPage: 999999999,
                },
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (result) => {
                this.broadcasts = (result.records as unknown as any[]).map(
                  (broadcast: any) => {
                    return {
                      ...broadcast,
                      conversion_pct:
                        Math.round(
                          (broadcast.conversions_total / broadcast.sent) *
                            100 *
                            10,
                        ) / 10 || 0,
                      tracking_window: Math.round(
                        (new Date(broadcast.removal_date_time).getTime() -
                          new Date(broadcast.send_date_time).getTime()) /
                          (1000 * 3600 * 24),
                      ),
                    };
                  },
                );
                this.broadcastsDataSource = new MatTableDataSource(
                  this.broadcasts,
                );
                this.broadcastsTable.renderRows();
                this.broadcastsDataSource.sort = this.sort;
              },
              (err) => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving broadcasts",
                );
              },
            );
        }
      });

    this.broadcasts = this.activatedRoute.snapshot.data.broadcasts.records.map(
      (broadcast: any) => {
        return {
          ...broadcast,
          conversion_pct:
            Math.round(
              (broadcast.conversions_total / broadcast.sent) * 100 * 10,
            ) / 10 || 0,
          tracking_window: Math.round(
            (new Date(broadcast.removal_date_time).getTime() -
              new Date(broadcast.send_date_time).getTime()) /
              (1000 * 3600 * 24),
          ),
        };
      },
    );

    const sortedbyConvPctBroadcast = this.broadcasts.sort((a: any, b: any) => {
      return b.conversion_pct - a.conversion_pct;
    });

    let counter = 1;
    for (let broadcast of sortedbyConvPctBroadcast) {
      broadcast.conversion_rank = counter;
      counter++;
    }

    this.broadcastsDataSource = new MatTableDataSource(this.broadcasts);

    this.broadcastsDataSource.filterPredicate = (data: any, filter: string) => {
      let found = false;

      const selectedFilterColumns = this.columns
        .filter((column) => column.selected)
        .map((column) => column.code);

      for (let column of selectedFilterColumns) {
        let rowValue = data[column];

        if (typeof rowValue === "number") {
          rowValue = rowValue.toString();
        }

        if (rowValue) {
          if (rowValue?.trim().toLowerCase().indexOf(filter) !== -1) {
            found = true;
          }
        }
      }

      return found;
    };

    this.options = {
      color: ["#a1c700"],
      xAxis: {
        type: "category",
        data: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      },
      yAxis: {
        type: "value",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      series: [
        {
          data: [820, 932, 901, 934, 1290, 1330, 1320],
          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 10,
          lineStyle: {
            shadowOffsetX: -5,
            shadowColor: "rgba(0, 0, 0, 0.5)",
            shadowBlur: 10,
            shadowOffsetY: 10,
          },
        },
      ],
    };
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  ngAfterViewInit() {
    this.broadcastsTable.renderRows();
    this.broadcastsDataSource.sort = this.sort;
  }

  get facilityIDs() {
    return this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue;
  }

  get currentApp() {
    return this.SessionService.currentAppValue;
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  applyFilter() {
    this.broadcastsDataSource.filter = this.searchValue.trim().toLowerCase();

    if (this.broadcastsDataSource.paginator) {
      this.broadcastsDataSource.paginator.firstPage();
    }
  }

  toggleFilterDialog() {
    this.Dialog.open(FilterTableDialogComponent, {
      data: {
        columns: this.columns,
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
        }
      });
  }

  exportToExcel() {
    this.uiService
      .waitForEvent(
        this.APIService.get(
          `app/quick-signals/${
            this.SessionService.currentAccountValue!.accountID
          }/${this.SessionService.currentAppValue!.id}`,
          {
            params: {
              startDate: this.filterForm.controls.startDate.value,
              endDate: this.filterForm.controls.endDate.value,
              facility_id: this.facilityIDs,
              page: 0,
              perPage: 999999999,
              forExport: true,
            },
            responseType: "blob" as "xlsx",
          },
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.fileSaver.save(
            <any>res,
            moment().format(
              "[LHPEBroadcastPerformance-]YYYY-MM-DD-HH-mm-ss[.xlsx]",
            ),
          );
        },
        (error) => {
          console.error("download error:", error);
        },
        () => {
          //console.log('Completed file download.')
        },
      );
  }

  onBroadcastClicked(row: any) {
    // this.BroastcastService.getBroadcast(
    //   this.currentAccount!.accountID,
    //   this.currentApp!.id,
    //   row.id,
    // ).subscribe((res) => {
    //   console.log(res);
    // });
    this.selectedBroadcast = row;

    if (this.notFirst) {
      this.options = {
        color: ["#a1c700"],
        xAxis: {
          type: "category",
          data: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        },
        yAxis: {
          type: "value",
        },

        series: [
          {
            data: [2000, 932, 4000, 7000, 1290, 900, 300],
            type: "line",
            smooth: true,
            symbol: "triangle",
            symbolSize: 10,
            lineStyle: {
              shadowOffsetX: -5,
              shadowColor: "rgba(0, 0, 0, 0.5)",
              shadowBlur: 10,
              shadowOffsetY: 10,
            },
          },
        ],
      };
      this.notFirst = false;
    } else {
      this.options = {
        color: ["#a1c700"],
        xAxis: {
          type: "category",
          data: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        },
        yAxis: {
          type: "value",
        },

        series: [
          {
            data: [820, 932, 901, 934, 1290, 1330, 1320],
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 10,
            lineStyle: {
              shadowOffsetX: -5,
              shadowColor: "rgba(0, 0, 0, 0.5)",
              shadowBlur: 10,
              shadowOffsetY: 10,
            },
          },
        ],
      };

      this.notFirst = true;
    }
  }
}
